import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, MapPin, Edit, Trash2, Share2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JournalDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const { theme } = useTheme();
  const [journal, setJournal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState('');
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    fetchJournal();
  }, [id]);

  useEffect(() => {
    // Fetch coaches lazily (only when needed)
    if (showShareDialog) {
      fetchCoaches();
    }
  }, [showShareDialog]);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API}/player/my-coaches`, {
        headers: getAuthHeader(),
        timeout: 3000
      });
      setCoaches(response.data.coaches || []);
    } catch (error) {
      console.error('Error fetching coaches:', error);
      setCoaches([]); // Set empty array on error
    }
  };

  const fetchJournal = async () => {
    try {
      const response = await axios.get(`${API}/journal/entries/${id}`, {
        headers: getAuthHeader()
        // No timeout - let it complete naturally
      });
      setJournal(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Journal fetch error:', error);
      toast.error('Failed to load journal. Please try again.');
      setLoading(false);
      // Don't auto-redirect - let user decide
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this journal entry?')) return;

    try {
      await axios.delete(`${API}/journal/entries/${id}`, {
        headers: getAuthHeader()
      });
      toast.success('Journal entry deleted');
      navigate('/player-dashboard');
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const shareWithCoach = async () => {
    // Check if Pro tier
    if (user?.subscription_tier === 'free') {
      toast.error('Upgrade to Pro to share journals with coaches');
      setTimeout(() => {
        navigate('/pricing');
      }, 2000);
      return;
    }
    
    if (!selectedCoach) {
      toast.error('Please select a coach');
      return;
    }

    setSharing(true);
    try {
      await axios.post(`${API}/journal/share-with-coach`, {
        journal_id: id,
        coach_id: selectedCoach
      }, {
        headers: getAuthHeader()
      });

      toast.success('Journal shared with coach!');
      setShowShareDialog(false);
      setSelectedCoach('');
    } catch (error) {
      toast.error('Failed to share journal');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Journal not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/player-dashboard')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} mb-4`}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Journal Entry</h1>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={() => setShowShareDialog(true)}>
                <Share2 className="w-4 h-4 mr-1" />
                Share with Coach
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate(`/journal/edit/${id}`)}>
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={handleDelete}>
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
          <div className="space-y-6">
            {/* Header Info */}
            <div className="flex items-center justify-between pb-6 border-b ${theme === 'dark' ? 'border-white/10' : 'border-gray-200'}">
              <div className="flex items-center space-x-4">
                <Calendar className="w-8 h-8 text-blue-400" />
                <div>
                  <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {new Date(journal.entry_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    {journal.session_type} session
                  </div>
                </div>
              </div>
              <span className={`px-4 py-2 rounded-full ${theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700'}`}>
                {journal.session_type}
              </span>
            </div>

            {/* Session Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-4 h-4 text-purple-400" />
                  <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Duration</span>
                </div>
                <div className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  {journal.duration_minutes} min
                </div>
              </div>
              
              {journal.location && (
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <MapPin className="w-4 h-4 text-green-400" />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Location</span>
                  </div>
                  <div className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {journal.location}
                  </div>
                </div>
              )}
            </div>

            {/* What I Practiced */}
            <div>
              <h3 className={`text-lg font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>What I Practiced</h3>
              <p className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} leading-relaxed`}>
                {journal.what_practiced}
              </p>
            </div>

            {/* What Went Well */}
            {journal.what_went_well && journal.what_went_well.length > 0 && (
              <div>
                <h3 className={`text-lg font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>What Went Well ✅</h3>
                <div className="flex flex-wrap gap-2">
                  {journal.what_went_well.map((item, idx) => (
                    <span key={`well-${idx}-${item}`} className="px-3 py-1 rounded-full bg-green-500/20 text-green-400">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Needs Improvement */}
            {journal.needs_improvement && journal.needs_improvement.length > 0 && (
              <div>
                <h3 className={`text-lg font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Needs Improvement ⚠️</h3>
                <div className="flex flex-wrap gap-2">
                  {journal.needs_improvement.map((item, idx) => (
                    <span key={`improve-${idx}-${item}`} className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-400">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Coach Notes */}
            {journal.coach_notes && (
              <div>
                <h3 className={`text-lg font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Coach Notes 👨‍🏫</h3>
                <div className={`p-4 rounded-lg ${theme === 'dark' ? 'bg-blue-500/10 border border-blue-500/30' : 'bg-blue-50 border border-blue-200'}`}>
                  <p className={theme === 'dark' ? 'text-blue-300' : 'text-blue-700'}>{journal.coach_notes}</p>
                </div>
              </div>
            )}

            {/* Mood, Energy, Confidence */}
            <div className="grid grid-cols-3 gap-4">
              <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Mood</div>
                <div className="text-3xl font-bold gradient-text">{journal.mood_rating}/10</div>
              </div>
              <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Energy</div>
                <div className="text-3xl font-bold gradient-text">{journal.energy_level}/10</div>
              </div>
              <div className={`p-4 rounded-lg text-center ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                <div className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Confidence</div>
                <div className="text-3xl font-bold gradient-text">{journal.confidence_level}/10</div>
              </div>
            </div>

            {/* Next Session Goals */}
            {journal.next_session_goals && journal.next_session_goals.length > 0 && (
              <div>
                <h3 className={`text-lg font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Next Session Goals 🎯</h3>
                <div className="flex flex-wrap gap-2">
                  {journal.next_session_goals.map((goal, idx) => (
                    <span key={`goal-${idx}-${goal}`} className="px-3 py-1 rounded-full bg-blue-500/20 text-blue-400">
                      {goal}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
        
        {/* Share Dialog */}
        {showShareDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowShareDialog(false)}>
            <Card className={`glass-card max-w-md w-full mx-4 ${theme === 'light' ? 'bg-white/95' : ''}`} onClick={(e) => e.stopPropagation()}>
              <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Share Journal with Coach</h3>
              
              {coaches.length > 0 ? (
                <div className="space-y-4">
                  <Select value={selectedCoach} onValueChange={setSelectedCoach}>
                    <SelectTrigger className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300'}>
                      <SelectValue placeholder="Select coach..." />
                    </SelectTrigger>
                    <SelectContent>
                      {coaches.map((coach) => (
                        <SelectItem key={coach.id} value={coach.id}>
                          {coach.name} - {coach.specialization?.join(', ') || 'Coach'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="flex gap-2">
                    <Button 
                      onClick={shareWithCoach}
                      disabled={sharing || !selectedCoach}
                      className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                    >
                      {sharing ? 'Sharing...' : 'Share'}
                    </Button>
                    <Button variant="outline" onClick={() => setShowShareDialog(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <p className={`mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    No coaches connected yet. Connect with a coach first to share journals.
                  </p>
                  <Button variant="outline" onClick={() => setShowShareDialog(false)}>
                    Close
                  </Button>
                </div>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
